import * as React from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeftIcon,
  CaretDownIcon,
  CaretRightIcon,
  CheckIcon,
  CopyrightIcon,
  GitBranchIcon,
  ImageIcon,
  ListBulletsIcon,
  LockSimpleIcon,
  PencilSimpleIcon,
  PlusIcon,
  ShuffleIcon,
  SlidersHorizontalIcon,
  TagIcon,
  TextTIcon,
  TranslateIcon,
  TrashIcon,
  UploadSimpleIcon,
  UsersThreeIcon,
  XIcon,
  type Icon
} from "@phosphor-icons/react";
import { cn } from "cn";
import { isEnterSubmit } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { DashedOutline } from "@libs/zapi-ui/ds/components/dashed-outline";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/ds/components/dialog";
import { Input } from "@libs/zapi-ui/ds/components/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput
} from "@libs/zapi-ui/ds/components/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import { Textarea } from "@libs/zapi-ui/ds/components/textarea";
import { ToggleGroup, ToggleGroupItem } from "@libs/zapi-ui/ds/components/toggle-group";

type ProjectPictureSource = { author: string; license: string; url: string };

type ProjectPicture = { url: string; alt: string; credit?: string; source?: ProjectPictureSource };

const pictureSource = (
  author: string | null | undefined,
  license: string | null | undefined,
  url: string | null | undefined
): ProjectPictureSource | undefined =>
  url && (author || license) ? { author: author ?? "", license: license ?? "", url } : undefined;

type ProjectDimension = {
  id: string;
  name: string;
  type: "text" | "dropdown";
  options?: string[];
  icon?: Icon;
};

// leo: Leo filled it, from a document when fileName is set, otherwise from the decision. user: the user set it.
// missing: Leo read the documents and found nothing for it.
type ProjectDimensionValue = {
  value: string | null;
  source: { kind: "leo"; fileName?: string } | { kind: "user" } | { kind: "missing" };
};

type ProjectTagDefinition = {
  id: string;
  name: string;
  type: "text" | "dropdown";
  options?: string[];
};

type ProjectTag = {
  id: string;
  name: string;
  type: "text" | "dropdown";
  options?: string[];
  value: string | null;
};

type ProjectVisibility = "organization" | "private";

type ProjectDetailsValues = {
  name: string;
  description: string;
  picture: ProjectPicture | null;
  language: string | null;
  versionName: string;
  // By dimension id; a dimension without an entry hasn't been looked at yet.
  dimensions: Record<string, ProjectDimensionValue>;
  tags?: ProjectTag[];
};

type ProjectDetailsLabels = {
  projectName: string;
  untitledProject: string;
  description: string;
  descriptionPlaceholder: string;
  language: string;
  languagePlaceholder: string;
  notSet: string;
  version: string;
  visibility: string;
  visibilityOrganization: string;
  visibilityPrivate: string;
  uploadPicture: string;
  shufflePicture: string;
  removePicture: string;
  showProjectDetails: string;
  addTag: string;
  tagWithValue: (name: string, value: string) => string;
  removeTagFromProject: (tag: string) => string;
  removeFromProject: string;
  allTagsOnProject: string;
  noTagsYet: string;
  manageTags: string;
  backToTags: string;
  edit: string;
  optionsOf: (name: string) => string;
  current: string;
  noOptions: string;
  noOptionsAddWithEdit: string;
  setTagValue: (name: string) => string;
  typeTagValue: (name: string) => string;
  pressEnter: string;
  manageTagsTitle: string;
  tagChangesDescription: string;
  createNewTag: string;
  done: string;
  close: string;
  deleteTagForEveryone: (name: string) => string;
  deleteForEveryone: string;
  deleteTagTitle: (name: string) => string;
  deleteTagDescription: (onThisProject: boolean) => string;
  editTagTitle: (name: string) => string;
  newTagTitle: string;
  newTagDescription: string;
  tagName: string;
  tagNamePlaceholder: string;
  tagNameTaken: (name: string) => string;
  tagType: string;
  tagTypeGroup: string;
  tagTypeOptions: string;
  tagTypeText: string;
  tagTypeOptionsHint: string;
  tagTypeTextHint: string;
  options: string;
  removeOption: (option: string) => string;
  addOption: string;
  newOption: string;
  addAnotherOption: string;
  typeAnOption: string;
  removedOptionWarning: string;
  anyText: string;
  noOptionsSummary: string;
  cancel: string;
  delete: string;
  saveChanges: string;
  createTag: string;
};

const defaultProjectDetailsLabels: ProjectDetailsLabels = {
  projectName: "Project name",
  untitledProject: "Untitled Project",
  description: "Description",
  descriptionPlaceholder: "Add a short description",
  language: "Language",
  languagePlaceholder: "Choose…",
  notSet: "Not set",
  version: "Version",
  visibility: "Visibility",
  visibilityOrganization: "Organization",
  visibilityPrivate: "Private",
  uploadPicture: "Upload picture",
  shufflePicture: "Shuffle picture",
  removePicture: "Remove picture",
  showProjectDetails: "Show the project details",
  addTag: "Add Tag",
  tagWithValue: (name, value) => `${name}: ${value}`,
  removeTagFromProject: (tag) => `Remove ${tag} from the project`,
  removeFromProject: "Remove from the project",
  allTagsOnProject: "All your organization’s tags are on this project.",
  noTagsYet: "Your organization has no tags yet.",
  manageTags: "Manage Tags",
  backToTags: "Back to tags",
  edit: "Edit",
  optionsOf: (name) => `${name} options`,
  current: "Current",
  noOptions: "No options yet.",
  noOptionsAddWithEdit: "No options yet. Add some with Edit.",
  setTagValue: (name) => `Set ${name}`,
  typeTagValue: (name) => `Type ${name.toLowerCase()}`,
  pressEnter: "Press Enter",
  manageTagsTitle: "Manage tags",
  tagChangesDescription: "Changes apply to every project in your organization.",
  createNewTag: "Create New Tag",
  done: "Done",
  close: "Close",
  deleteTagForEveryone: (name) => `Delete ${name} for everyone`,
  deleteForEveryone: "Delete for everyone",
  deleteTagTitle: (name) => `Delete “${name}” for everyone?`,
  deleteTagDescription: (onThisProject) =>
    `It will be removed from every project that has it${
      onThisProject ? ", this one included" : ""
    }, and won’t be offered again.`,
  editTagTitle: (name) => `Edit “${name}”`,
  newTagTitle: "New tag",
  newTagDescription: "Every project in your organization can use it.",
  tagName: "Name",
  tagNamePlaceholder: "e.g. Stage, Region, Deal lead",
  tagNameTaken: (name) => `Your organization already has a “${name}” tag.`,
  tagType: "Type",
  tagTypeGroup: "Tag type",
  tagTypeOptions: "Options",
  tagTypeText: "Text",
  tagTypeOptionsHint: "Each project picks one of these options.",
  tagTypeTextHint: "Each project types its own value.",
  options: "Options",
  removeOption: (option) => `Remove ${option}`,
  addOption: "Add option",
  newOption: "New option",
  addAnotherOption: "Add another option",
  typeAnOption: "Type an option",
  removedOptionWarning: "Projects that picked a removed option lose this tag.",
  anyText: "Any text",
  noOptionsSummary: "No options yet",
  cancel: "Cancel",
  delete: "Delete",
  saveChanges: "Save Changes",
  createTag: "Create Tag"
};

type ProjectDetailsProps = {
  values: ProjectDetailsValues;
  languages: string[];
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onLanguageChange: (language: string) => void;
  // Without it the version name can't be edited.
  onVersionNameChange?: (name: string) => void;
  onTagsChange?: (tags: ProjectTag[]) => void;
  visibility?: ProjectVisibility;
  onVisibilityChange?: (visibility: ProjectVisibility) => void;
  tagLibrary?: ProjectTagDefinition[];
  onCreateTagDefinition?: (definition: Omit<ProjectTagDefinition, "id">) => void;
  onUpdateTagDefinition?: (definitionId: string, definition: Omit<ProjectTagDefinition, "id">) => void;
  onDeleteTagDefinition?: (definitionId: string) => void;
  canManageTags?: boolean;
  // Left out until there is content to pick a picture from; the cover then offers only Upload.
  onShufflePicture?: () => void;
  onUploadPicture?: (file: File) => void;
  onRemovePicture?: () => void;
  pictureAccept?: string;
  pending?: boolean;
  // Shown as it is: nothing can be edited.
  readOnly?: boolean;
  labels?: Partial<ProjectDetailsLabels>;
  className?: string;
};

const NOT_SET = "__not-set__";

// Quiet until hovered or focused, so the panel reads as the project rather than a form.
const quietControl =
  "h-8 w-full justify-start border-transparent bg-transparent px-2 shadow-none hover:bg-foreground/[0.04] focus-visible:border-transparent focus-visible:bg-foreground/[0.04] focus-visible:ring-0";

function ProjectPictureThumb({ picture, size = "lg" }: { picture: ProjectPicture | null; size?: "lg" | "md" | "sm" }) {
  const [failedUrl, setFailedUrl] = React.useState<string | null>(null);
  const box = size === "lg" ? "size-16 rounded-xl" : size === "md" ? "size-12 rounded-lg" : "size-9 rounded-lg";

  if (picture && picture.url !== failedUrl) {
    return (
      <img
        key={picture.url}
        src={picture.url}
        alt={picture.alt}
        onError={() => setFailedUrl(picture.url)}
        className={cn(box, "animate-in object-cover ring-1 ring-foreground/10 duration-300 zoom-in-90 fade-in")}
      />
    );
  }

  return (
    <span className={cn(box, "relative flex items-center justify-center text-muted-foreground")}>
      <DashedOutline
        radius={size === "lg" ? "var(--radius-xl)" : "var(--radius-lg)"}
        className={size === "sm" ? "[&_rect]:stroke-[1]" : undefined}
      />
      <ImageIcon className="size-5" aria-hidden="true" />
    </span>
  );
}

const CREDIT_HOVER_MS = 600;
const CREDIT_CLOSE_GRACE_MS = 150;

function PictureCredit({
  picture,
  className,
  children
}: {
  picture: ProjectPicture | null;
  className?: string;
  children: React.ReactNode;
}) {
  const [at, setAt] = React.useState<{ x: number; y: number } | null>(null);
  const restTimer = React.useRef<number | undefined>(undefined);
  const closeTimer = React.useRef<number | undefined>(undefined);
  const source = picture?.source;
  const credit = [source?.author, source?.license].filter(Boolean).join(" · ");

  React.useEffect(
    () => () => {
      window.clearTimeout(restTimer.current);
      window.clearTimeout(closeTimer.current);
    },
    []
  );
  React.useEffect(() => setAt(null), [picture?.url]);

  const stay = () => window.clearTimeout(closeTimer.current);
  const leave = () => {
    window.clearTimeout(restTimer.current);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setAt(null), CREDIT_CLOSE_GRACE_MS);
  };

  return (
    <div
      className={className}
      onPointerEnter={stay}
      onPointerMove={(event) => {
        stay();
        if (!credit || at || !event.currentTarget.contains(event.target as Node)) return;
        const point = { x: event.clientX, y: event.clientY };
        window.clearTimeout(restTimer.current);
        restTimer.current = window.setTimeout(() => setAt(point), CREDIT_HOVER_MS);
      }}
      onPointerLeave={leave}
      onPointerDown={(event) => {
        if (!event.currentTarget.contains(event.target as Node)) return;
        window.clearTimeout(restTimer.current);
        setAt(null);
      }}
    >
      {children}
      {at &&
        source &&
        credit &&
        createPortal(
          <div
            data-slot="picture-credit"
            role="tooltip"
            style={{ left: at.x, top: at.y + 12 }}
            className="fixed z-50 animate-in rounded-lg bg-greyscale-950 px-2.5 py-1 typo-label-sm whitespace-nowrap text-greyscale-0 shadow-md duration-150 fade-in"
            onPointerEnter={stay}
            onPointerLeave={leave}
          >
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 underline-offset-2 hover:underline"
            >
              <CopyrightIcon className="size-3.5 shrink-0" aria-hidden="true" />
              {credit}
            </a>
          </div>,
          document.body
        )}
    </div>
  );
}

function ProjectCoverField({
  picture,
  accept = "image/*",
  onShuffle,
  onUpload,
  onRemove,
  text,
  className
}: {
  picture: ProjectPicture | null;
  // The image types the file chooser offers.
  accept?: string;
  onShuffle?: () => void;
  onUpload?: (file: File) => void;
  onRemove?: () => void;
  text: ProjectDetailsLabels;
  className?: string;
}) {
  const fileInput = React.useRef<HTMLInputElement>(null);
  const [failedUrl, setFailedUrl] = React.useState<string | null>(null);
  const imageUrl = picture && picture.url !== failedUrl ? picture.url : undefined;

  return (
    <div
      data-empty={!imageUrl || undefined}
      className={cn("group/cover relative overflow-hidden rounded-2xl bg-muted data-empty:bg-transparent", className)}
    >
      {imageUrl ? (
        <PictureCredit picture={picture} className="absolute inset-0">
          <img
            key={imageUrl}
            src={imageUrl}
            alt={picture?.alt ?? ""}
            onError={() => setFailedUrl(imageUrl)}
            className="size-full animate-in object-cover duration-500 fade-in"
          />
        </PictureCredit>
      ) : (
        <button
          type="button"
          aria-label={text.uploadPicture}
          disabled={!onUpload}
          className="group/empty absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-canvas text-muted-foreground transition-colors duration-200 outline-none hover:text-foreground focus-visible:text-foreground disabled:pointer-events-none"
          onClick={() => fileInput.current?.click()}
        >
          <DashedOutline
            radius="var(--radius-2xl)"
            className="group-hover/empty:text-foreground/40 group-focus-visible/empty:text-foreground/40"
          />
          <ImageIcon weight="light" className="size-6" aria-hidden="true" />
        </button>
      )}
      <div className="absolute end-2 top-2 flex gap-1 opacity-0 transition-opacity duration-200 group-focus-within/cover:opacity-100 group-hover/cover:opacity-100">
        {onShuffle && (
          <Button
            variant="outline"
            size="icon-sm"
            shape="pill"
            aria-label={text.shufflePicture}
            title={text.shufflePicture}
            className="bg-background/90 backdrop-blur-sm"
            onClick={onShuffle}
          >
            <ShuffleIcon />
          </Button>
        )}
        {onUpload && (
          <Button
            variant="outline"
            size="icon-sm"
            shape="pill"
            aria-label={text.uploadPicture}
            title={text.uploadPicture}
            className="bg-background/90 backdrop-blur-sm"
            onClick={() => fileInput.current?.click()}
          >
            <UploadSimpleIcon />
          </Button>
        )}
        {imageUrl && onRemove && (
          <Button
            variant="outline"
            size="icon-sm"
            shape="pill"
            aria-label={text.removePicture}
            title={text.removePicture}
            className="bg-background/90 backdrop-blur-sm"
            onClick={onRemove}
          >
            <TrashIcon />
          </Button>
        )}
      </div>
      <input
        ref={fileInput}
        type="file"
        accept={accept}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) onUpload?.(file);
        }}
      />
    </div>
  );
}

// Grows a textarea to its content where the browser can't size it on its own (field-sizing).
function fitHeight(field: HTMLTextAreaElement | null) {
  if (!field) return;
  field.style.height = "auto";
  field.style.height = `${field.scrollHeight + field.offsetHeight - field.clientHeight}px`;
}

// Saves when the field loses focus or on Enter, so typing doesn't write on every key. multiline wraps long text
// instead of cutting it off.
function SavingInput({
  value,
  multiline,
  onSave,
  className,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"input">, "value" | "defaultValue" | "onBlur"> & {
  value: string;
  multiline?: boolean;
  onSave: (value: string) => void;
}) {
  const save = (field: HTMLInputElement | HTMLTextAreaElement) => {
    if (props.readOnly) return;
    const next = field.value.trim();
    if (!next && props.required) {
      field.value = value;
      return;
    }
    if (next !== value) onSave(next);
  };
  if (multiline) {
    return (
      <Textarea
        key={value}
        ref={fitHeight}
        onInput={(event) => fitHeight(event.currentTarget)}
        id={props.id}
        required={props.required}
        readOnly={props.readOnly}
        aria-label={props["aria-label"]}
        placeholder={props.placeholder}
        defaultValue={value}
        rows={1}
        className={cn("field-sizing-content min-h-0 resize-none", className)}
        onBlur={(event) => save(event.target)}
        onKeyDown={(event) => {
          if (isEnterSubmit(event, { allowShift: true })) {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
      />
    );
  }
  return (
    <Input
      key={value}
      {...props}
      defaultValue={value}
      className={className}
      onBlur={(event) => save(event.target)}
      onKeyDown={(event) => {
        if (isEnterSubmit(event, { allowShift: true })) event.currentTarget.blur();
      }}
    />
  );
}

function PropertySelect({
  id,
  value,
  options,
  placeholder,
  required = false,
  disabled = false,
  text,
  className,
  onChange
}: {
  id: string;
  value: string | null;
  options: string[];
  placeholder: string;
  disabled?: boolean;
  // Always has a value: no "Not set" choice.
  required?: boolean;
  text: ProjectDetailsLabels;
  className?: string;
  onChange: (value: string | null) => void;
}) {
  return (
    <Select
      items={[
        ...(required ? [] : [{ value: NOT_SET, label: placeholder }]),
        ...options.map((option) => ({ value: option, label: option }))
      ]}
      value={value ?? NOT_SET}
      disabled={disabled}
      onValueChange={(next) => typeof next === "string" && onChange(next === NOT_SET ? null : next)}
    >
      <SelectTrigger
        id={id}
        className={cn(
          quietControl,
          "typo-body-sm [&>svg:last-child]:opacity-0 hover:[&>svg:last-child]:opacity-100 focus-visible:[&>svg:last-child]:opacity-100",
          className,
          !value && "font-normal text-muted-foreground"
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {!required && <SelectItem value={NOT_SET}>{text.notSet}</SelectItem>}
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

let tagCount = 0;
const newTagId = () => `tag-${Date.now().toString(36)}-${++tagCount}`;

function InlineAddField({
  value,
  onValueChange,
  onAdd,
  submitLabel,
  autoFocus,
  addOnBlur = false,
  text,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"input">, "value" | "onChange"> & {
  value: string;
  onValueChange: (value: string) => void;
  onAdd: (value: string) => void;
  submitLabel: string;
  // Adds what is typed when the field loses focus, so a typed option isn't lost on Create.
  addOnBlur?: boolean;
  text: ProjectDetailsLabels;
}) {
  const ready = value.trim().length > 0;
  const add = () => {
    if (ready) onAdd(value.trim());
  };
  return (
    <InputGroup>
      <InputGroupInput
        {...props}
        value={value}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        onChange={(event) => onValueChange(event.target.value)}
        onKeyDown={(event) => {
          if (isEnterSubmit(event, { allowShift: true })) {
            event.preventDefault();
            add();
          }
        }}
        onBlur={addOnBlur ? add : undefined}
      />
      <InputGroupAddon align="inline-end">
        <InputGroupButton
          type="button"
          size="icon-xs"
          variant={ready ? "default" : "ghost"}
          className="rounded-md"
          disabled={!ready}
          aria-label={submitLabel}
          title={text.pressEnter}
          onMouseDown={(event) => event.preventDefault()}
          onClick={add}
        >
          <CheckIcon weight="bold" />
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}

// The type is fixed once the tag exists, since projects already hold values of it.
function TagDefinitionForm({
  definition,
  takenNames = [],
  onSave,
  onCancel,
  onDelete,
  text
}: {
  definition?: ProjectTagDefinition;
  // A tag can't take a name the organization already uses, so tags don't split into "Stage" and "stage".
  takenNames?: string[];
  onSave: (definition: Omit<ProjectTagDefinition, "id">) => void;
  onCancel: () => void;
  onDelete?: () => void;
  text: ProjectDetailsLabels;
}) {
  const formId = React.useId();
  const [name, setName] = React.useState(definition?.name ?? "");
  const [type, setType] = React.useState<ProjectTag["type"]>(definition?.type ?? "dropdown");
  const [options, setOptions] = React.useState<string[]>(definition?.options ?? []);
  const [option, setOption] = React.useState("");
  const addOption = (next: string) => {
    if (!options.includes(next)) setOptions([...options, next]);
    setOption("");
  };
  const taken = takenNames.find(
    (existing) =>
      existing.toLowerCase() === name.trim().toLowerCase() && existing.toLowerCase() !== definition?.name.toLowerCase()
  );
  const canSave = name.trim().length > 0 && !taken && (type === "text" || options.length > 0);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSave) return;
        onSave({
          name: name.trim(),
          type,
          options: type === "dropdown" ? options : undefined
        });
      }}
    >
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${formId}-name`} className="typo-label-sm text-muted-foreground">
          {text.tagName}
        </label>
        <Input
          id={`${formId}-name`}
          value={name}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={!definition}
          placeholder={text.tagNamePlaceholder}
          aria-invalid={Boolean(taken) || undefined}
          onChange={(event) => setName(event.target.value)}
        />
        {taken && <p className="typo-caption text-destructive">{text.tagNameTaken(taken)}</p>}
      </div>
      {!definition && (
        <div className="flex flex-col gap-1.5">
          <span className="typo-label-sm text-muted-foreground">{text.tagType}</span>
          <ToggleGroup
            value={[type]}
            onValueChange={(next) => next[0] && setType(next[0] as ProjectTag["type"])}
            spacing={0}
            aria-label={text.tagTypeGroup}
            className="w-full rounded-full bg-muted p-0.5"
          >
            {(["dropdown", "text"] as const).map((kind) => (
              <ToggleGroupItem
                key={kind}
                value={kind}
                size="sm"
                className="h-7 flex-1 rounded-full! text-muted-foreground aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-xs"
              >
                {kind === "text" ? text.tagTypeText : text.tagTypeOptions}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <p className="typo-caption text-muted-foreground">
            {type === "text" ? text.tagTypeTextHint : text.tagTypeOptionsHint}
          </p>
        </div>
      )}
      {type === "dropdown" && (
        <div className="flex flex-col gap-1.5">
          <span className="typo-label-sm text-muted-foreground">{text.options}</span>
          {options.length > 0 && (
            <ul className="flex flex-wrap gap-1.5" aria-label={text.options}>
              {options.map((entry) => (
                <li
                  key={entry}
                  className="inline-flex h-7 items-center gap-0.5 rounded-full bg-grayscale-100 ps-2.5 typo-label-sm text-foreground"
                >
                  {entry}
                  <button
                    type="button"
                    aria-label={text.removeOption(entry)}
                    className="me-0.5 flex size-5 items-center justify-center rounded-full text-muted-foreground outline-none hover:bg-grayscale-300 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
                    onClick={() => setOptions(options.filter((existing) => existing !== entry))}
                  >
                    <XIcon className="size-3" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <InlineAddField
            value={option}
            onValueChange={setOption}
            onAdd={addOption}
            addOnBlur
            submitLabel={text.addOption}
            aria-label={text.newOption}
            placeholder={options.length > 0 ? text.addAnotherOption : text.typeAnOption}
            text={text}
          />
          {definition && <p className="typo-caption text-muted-foreground">{text.removedOptionWarning}</p>}
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        {onDelete && (
          <Button type="button" variant="ghost" size="sm" shape="pill" className="text-destructive" onClick={onDelete}>
            <TrashIcon data-icon="inline-start" />
            {text.delete}
          </Button>
        )}
        <div className="ms-auto flex gap-2">
          <Button type="button" variant="ghost" size="sm" shape="pill" onClick={onCancel}>
            {text.cancel}
          </Button>
          <Button type="submit" size="sm" shape="pill" disabled={!canSave}>
            {definition ? <CheckIcon data-icon="inline-start" /> : <PlusIcon data-icon="inline-start" />}
            {definition ? text.saveChanges : text.createTag}
          </Button>
        </div>
      </div>
    </form>
  );
}

const tagRowClassName =
  "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-start outline-none transition-colors hover:bg-accent focus-visible:bg-accent aria-current:bg-accent/60";

function describeDefinition(definition: ProjectTagDefinition, text: ProjectDetailsLabels) {
  if (definition.type !== "dropdown") return text.anyText;
  const options = definition.options ?? [];
  return options.length > 0 ? options.join(" · ") : text.noOptionsSummary;
}

function TagRowIcon({ icon: RowIcon, dashed = false }: { icon: Icon; dashed?: boolean }) {
  return (
    <span
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground",
        dashed
          ? "border border-dashed border-foreground/25"
          : "bg-muted group-hover/tag:bg-background group-focus-visible/tag:bg-background"
      )}
    >
      <RowIcon className="size-[18px]" aria-hidden="true" />
    </span>
  );
}

const toProjectTag = (definition: ProjectTagDefinition, value: string): ProjectTag => ({
  id: newTagId(),
  name: definition.name,
  type: definition.type,
  options: definition.options,
  value
});

const sameName = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

function TagValuePicker({
  name,
  type,
  options = [],
  value,
  onBack,
  onEdit,
  onPick,
  text
}: {
  name: string;
  type: ProjectTag["type"];
  options?: string[];
  value?: string | null;
  onBack?: () => void;
  onEdit?: () => void;
  onPick: (value: string) => void;
  text: ProjectDetailsLabels;
}) {
  const [typed, setTyped] = React.useState(type === "text" ? value ?? "" : "");
  return (
    <div className="flex flex-col gap-1">
      <div className={cn("flex min-h-8 items-center gap-1 pe-0.5", onBack ? "ps-1" : "ps-2.5")}>
        {onBack && (
          <Button variant="ghost" size="icon-sm" shape="pill" aria-label={text.backToTags} onClick={onBack}>
            <ArrowLeftIcon className="rtl:-scale-x-100" />
          </Button>
        )}
        <span className="min-w-0 flex-1 truncate typo-label-md text-foreground">{name}</span>
        {onEdit && (
          <Button variant="ghost" size="sm" shape="pill" className="text-muted-foreground" onClick={onEdit}>
            <PencilSimpleIcon data-icon="inline-start" />
            {text.edit}
          </Button>
        )}
      </div>
      {type === "dropdown" ? (
        options.length > 0 ? (
          <ul className="flex flex-col gap-0.5" aria-label={text.optionsOf(name)}>
            {options.map((entry) => (
              <li key={entry}>
                <button
                  type="button"
                  aria-current={entry === value || undefined}
                  onClick={() => onPick(entry)}
                  className={tagRowClassName}
                >
                  <span className="min-w-0 flex-1 truncate typo-body-sm text-foreground">{entry}</span>
                  {entry === value && (
                    <CheckIcon className="size-4 shrink-0 text-foreground" aria-label={text.current} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-2.5 py-3 typo-body-sm text-muted-foreground">
            {onEdit ? text.noOptionsAddWithEdit : text.noOptions}
          </p>
        )
      ) : (
        <div className="p-1.5">
          <InlineAddField
            value={typed}
            onValueChange={setTyped}
            onAdd={onPick}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            submitLabel={text.setTagValue(name)}
            aria-label={name}
            placeholder={text.typeTagValue(name)}
            text={text}
          />
        </div>
      )}
    </div>
  );
}

function TagPicker({
  library,
  usedNames,
  onAdd,
  onManage,
  text
}: {
  library: ProjectTagDefinition[];
  usedNames: string[];
  onAdd: (tag: ProjectTag) => void;
  onManage?: (definitionId?: string) => void;
  text: ProjectDetailsLabels;
}) {
  const [picked, setPicked] = React.useState<ProjectTagDefinition | null>(null);
  const available = library.filter((definition) => !usedNames.some((name) => sameName(name, definition.name)));

  if (picked) {
    return (
      <TagValuePicker
        name={picked.name}
        type={picked.type}
        options={picked.options}
        onBack={() => setPicked(null)}
        onEdit={onManage && (() => onManage(picked.id))}
        onPick={(value) => onAdd(toProjectTag(picked, value))}
        text={text}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {available.length > 0 ? (
        <ul className="flex flex-col gap-0.5">
          {available.map((definition) => (
            <li key={definition.id}>
              <button type="button" onClick={() => setPicked(definition)} className={cn(tagRowClassName, "group/tag")}>
                <TagRowIcon icon={definition.type === "dropdown" ? ListBulletsIcon : TextTIcon} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate typo-label-md text-foreground">{definition.name}</span>
                  <span className="truncate typo-caption text-muted-foreground">
                    {describeDefinition(definition, text)}
                  </span>
                </span>
                <CaretRightIcon
                  className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/tag:opacity-100 group-focus-visible/tag:opacity-100 rtl:-scale-x-100"
                  aria-hidden="true"
                />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-2.5 py-3 typo-body-sm text-muted-foreground">
          {library.length > 0 ? text.allTagsOnProject : text.noTagsYet}
        </p>
      )}
      {onManage && (
        <div className="mt-1 border-t pt-2">
          <Button variant="outline" size="sm" shape="pill" className="w-full" onClick={() => onManage()}>
            <SlidersHorizontalIcon data-icon="inline-start" />
            {text.manageTags}
          </Button>
        </div>
      )}
    </div>
  );
}

type ManageTagsView =
  | { kind: "list" }
  | { kind: "create" }
  | { kind: "edit"; definition: ProjectTagDefinition }
  | { kind: "delete"; definition: ProjectTagDefinition; from: "list" | "edit" };

// Deleting a tag, or an option of it, takes the tag off the projects that have it.
function ManageTagsDialog({
  open,
  onOpenChange,
  library,
  focusId,
  creating = false,
  usedNames = [],
  onCreate,
  onUpdate,
  onDelete,
  labels
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  library: ProjectTagDefinition[];
  focusId?: string;
  creating?: boolean;
  usedNames?: string[];
  onCreate?: (definition: Omit<ProjectTagDefinition, "id">) => void;
  onUpdate?: (definitionId: string, definition: Omit<ProjectTagDefinition, "id">) => void;
  onDelete?: (definitionId: string) => void;
  labels?: Partial<ProjectDetailsLabels>;
}) {
  const text = { ...defaultProjectDetailsLabels, ...labels };
  const [view, setView] = React.useState<ManageTagsView>({ kind: "list" });
  // Opened straight on one tag (focusId) or on creating one, the dialog closes when that is done.
  const direct = React.useRef(false);
  const libraryRef = React.useRef(library);
  libraryRef.current = library;
  React.useEffect(() => {
    if (!open) return;
    const focused = focusId ? libraryRef.current.find((definition) => definition.id === focusId) : undefined;
    direct.current = Boolean(focused) || creating;
    setView(focused ? { kind: "edit", definition: focused } : creating ? { kind: "create" } : { kind: "list" });
  }, [open, focusId, creating]);
  const finish = () => (direct.current ? onOpenChange(false) : setView({ kind: "list" }));
  const takenNames = library.map((definition) => definition.name);
  const icon = (
    <span className="mb-1 flex size-10 items-center justify-center rounded-xl bg-muted text-muted-foreground">
      <TagIcon weight="duotone" className="size-5" />
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent initialFocus={false} closeLabel={text.close} className="gap-5 rounded-2xl sm:max-w-md">
        {view.kind === "delete" && onDelete ? (
          <>
            <DialogHeader>
              <DialogTitle className="typo-h5">{text.deleteTagTitle(view.definition.name)}</DialogTitle>
              <DialogDescription className="typo-body-sm">
                {text.deleteTagDescription(usedNames.some((name) => sameName(name, view.definition.name)))}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                shape="pill"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onClick={() =>
                  setView(view.from === "edit" ? { kind: "edit", definition: view.definition } : { kind: "list" })
                }
              >
                {text.cancel}
              </Button>
              <Button
                variant="destructive"
                shape="pill"
                onClick={() => {
                  onDelete(view.definition.id);
                  finish();
                }}
              >
                <TrashIcon data-icon="inline-start" />
                {text.delete}
              </Button>
            </DialogFooter>
          </>
        ) : view.kind === "edit" && onUpdate ? (
          <>
            <DialogHeader>
              {icon}
              <DialogTitle className="typo-h5">{text.editTagTitle(view.definition.name)}</DialogTitle>
              <DialogDescription className="typo-body-sm">{text.tagChangesDescription}</DialogDescription>
            </DialogHeader>
            <TagDefinitionForm
              key={view.definition.id}
              definition={view.definition}
              takenNames={library
                .filter((definition) => definition.id !== view.definition.id)
                .map((definition) => definition.name)}
              onSave={(definition) => {
                onUpdate(view.definition.id, definition);
                finish();
              }}
              onCancel={finish}
              onDelete={
                onDelete &&
                (() =>
                  setView({
                    kind: "delete",
                    definition: view.definition,
                    from: "edit"
                  }))
              }
              text={text}
            />
          </>
        ) : view.kind === "create" && onCreate ? (
          <>
            <DialogHeader>
              {icon}
              <DialogTitle className="typo-h5">{text.newTagTitle}</DialogTitle>
              <DialogDescription className="typo-body-sm">{text.newTagDescription}</DialogDescription>
            </DialogHeader>
            <TagDefinitionForm
              takenNames={takenNames}
              onSave={(definition) => {
                onCreate(definition);
                finish();
              }}
              onCancel={finish}
              text={text}
            />
          </>
        ) : (
          <>
            <DialogHeader>
              {icon}
              <DialogTitle className="typo-h5">{text.manageTagsTitle}</DialogTitle>
              <DialogDescription className="typo-body-sm">{text.tagChangesDescription}</DialogDescription>
            </DialogHeader>
            {library.length === 0 ? (
              <p className="py-6 text-center typo-body-sm text-muted-foreground">{text.noTagsYet}</p>
            ) : (
              <ul className="-mx-2.5 flex max-h-80 flex-col gap-0.5 overflow-y-auto">
                {library.map((definition) => (
                  <li key={definition.id} className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={!onUpdate}
                      onClick={() => setView({ kind: "edit", definition })}
                      className={cn(tagRowClassName, "group/tag min-w-0 flex-1 disabled:pointer-events-none")}
                    >
                      <TagRowIcon icon={definition.type === "dropdown" ? ListBulletsIcon : TextTIcon} />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate typo-label-md text-foreground">{definition.name}</span>
                        <span className="truncate typo-caption text-muted-foreground">
                          {describeDefinition(definition, text)}
                        </span>
                      </span>
                      {onUpdate && (
                        <PencilSimpleIcon
                          className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/tag:opacity-100 group-focus-visible/tag:opacity-100"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                    {onDelete && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        shape="pill"
                        aria-label={text.deleteTagForEveryone(definition.name)}
                        title={text.deleteForEveryone}
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => setView({ kind: "delete", definition, from: "list" })}
                      >
                        <TrashIcon />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter className="sm:justify-between">
              {onCreate ? (
                <Button variant="outline" shape="pill" onClick={() => setView({ kind: "create" })}>
                  <PlusIcon data-icon="inline-start" />
                  {text.createNewTag}
                </Button>
              ) : (
                <span />
              )}
              <Button shape="pill" onClick={() => onOpenChange(false)}>
                {text.done}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TagChip({
  tag,
  definition,
  onChange,
  onRemove,
  onManage,
  text
}: {
  tag: ProjectTag;
  // The organization's tag it comes from, for its current options and Edit.
  definition?: ProjectTagDefinition;
  onChange: (tag: ProjectTag) => void;
  onRemove: () => void;
  onManage?: (definitionId: string) => void;
  text: ProjectDetailsLabels;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <span className="inline-flex h-8 max-w-80 items-center rounded-full bg-grayscale-100 typo-label-md text-foreground transition-colors hover:bg-grayscale-200 has-[[data-popup-open]]:bg-grayscale-200">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          title={tag.value ? tag.name : undefined}
          aria-label={tag.value ? text.tagWithValue(tag.name, tag.value) : tag.name}
          className="flex h-full min-w-0 items-center rounded-s-full ps-3 pe-1 outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          {tag.value ? (
            <span className="truncate">{tag.value}</span>
          ) : (
            <span className="shrink-0 text-muted-foreground">{tag.name}</span>
          )}
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 gap-0 rounded-xl p-2 shadow-lg">
          <TagValuePicker
            name={definition?.name ?? tag.name}
            type={tag.type}
            options={definition?.options ?? tag.options}
            value={tag.value}
            onEdit={
              definition && onManage
                ? () => {
                    setOpen(false);
                    onManage(definition.id);
                  }
                : undefined
            }
            onPick={(value) => {
              onChange({
                ...tag,
                value,
                options: definition?.options ?? tag.options
              });
              setOpen(false);
            }}
            text={text}
          />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        aria-label={text.removeTagFromProject(tag.value ?? tag.name)}
        title={text.removeFromProject}
        onClick={onRemove}
        className="me-1 flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-grayscale-300 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <XIcon className="size-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}

function TagSuggestion({
  definition,
  onAdd,
  onManage,
  text
}: {
  definition: ProjectTagDefinition;
  onAdd: (tag: ProjectTag) => void;
  onManage?: (definitionId: string) => void;
  text: ProjectDetailsLabels;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="group/suggestion relative inline-flex h-8 max-w-60 items-center rounded-full px-3 typo-label-md text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset data-[popup-open]:text-foreground">
        <DashedOutline
          radius="1rem"
          className="group-hover/suggestion:text-foreground/40 group-data-[popup-open]/suggestion:text-foreground/40"
        />
        <span className="truncate">{definition.name}</span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 gap-0 rounded-xl p-2 shadow-lg">
        <TagValuePicker
          name={definition.name}
          type={definition.type}
          options={definition.options}
          onEdit={
            onManage
              ? () => {
                  setOpen(false);
                  onManage(definition.id);
                }
              : undefined
          }
          onPick={(value) => {
            onAdd(toProjectTag(definition, value));
            setOpen(false);
          }}
          text={text}
        />
      </PopoverContent>
    </Popover>
  );
}

function ProjectTagsField({
  tags,
  library = [],
  pending = false,
  onTagsChange,
  onCreateDefinition,
  onUpdateDefinition,
  onDeleteDefinition,
  canManageTags = true,
  labels,
  className
}: {
  tags: ProjectTag[];
  library?: ProjectTagDefinition[];
  pending?: boolean;
  onTagsChange: (tags: ProjectTag[]) => void;
  onCreateDefinition?: (definition: Omit<ProjectTagDefinition, "id">) => void;
  onUpdateDefinition?: (definitionId: string, definition: Omit<ProjectTagDefinition, "id">) => void;
  onDeleteDefinition?: (definitionId: string) => void;
  canManageTags?: boolean;
  labels?: Partial<ProjectDetailsLabels>;
  className?: string;
}) {
  const text = { ...defaultProjectDetailsLabels, ...labels };
  const [adding, setAdding] = React.useState(false);
  const [managing, setManaging] = React.useState<{
    focusId?: string;
    creating?: boolean;
  } | null>(null);
  const canManage = canManageTags && Boolean(onCreateDefinition || onUpdateDefinition || onDeleteDefinition);
  const manage = canManage
    ? (focusId?: string) => {
        setAdding(false);
        setManaging({ focusId });
      }
    : undefined;
  const usedNames = tags.map((tag) => tag.name);
  const definitionFor = (tag: ProjectTag) => library.find((definition) => sameName(definition.name, tag.name));
  const suggestions = library.filter((definition) => !usedNames.some((name) => sameName(name, definition.name)));

  if (pending) {
    return (
      <div className={cn("flex flex-wrap gap-2", className)}>
        {[28, 22].map((width, index) => (
          <Skeleton key={index} className="h-8 rounded-full" style={{ width: `${width * 4}px` }} />
        ))}
      </div>
    );
  }

  return (
    <div data-slot="project-tags-field" className={cn("flex flex-wrap items-center gap-2", className)}>
      {tags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          definition={definitionFor(tag)}
          onManage={manage}
          onChange={(next) => onTagsChange(tags.map((existing) => (existing.id === next.id ? next : existing)))}
          onRemove={() => onTagsChange(tags.filter((existing) => existing.id !== tag.id))}
          text={text}
        />
      ))}
      {suggestions.length === 0 && !canManage ? null : suggestions.length === 0 ? (
        <Button
          variant="outline"
          size="sm"
          shape="pill"
          className="h-8"
          onClick={() => setManaging({ creating: true })}
        >
          <PlusIcon data-icon="inline-start" />
          {text.addTag}
        </Button>
      ) : (
        <Popover open={adding} onOpenChange={setAdding}>
          <PopoverTrigger render={<Button variant="outline" size="sm" shape="pill" className="h-8" />}>
            <PlusIcon data-icon="inline-start" />
            {text.addTag}
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 gap-0 rounded-xl p-2 shadow-lg">
            <TagPicker
              library={library}
              usedNames={usedNames}
              onAdd={(tag) => {
                onTagsChange([...tags, tag]);
                setAdding(false);
              }}
              onManage={manage}
              text={text}
            />
          </PopoverContent>
        </Popover>
      )}
      {suggestions.length > 0 && (
        <div className="flex h-8 min-w-0 flex-1 basis-0 flex-wrap gap-2 overflow-hidden">
          {/* Leads the line, so a chip too wide for the space left wraps out of view instead of being cut off. */}
          <span aria-hidden="true" className="-me-2 h-8 w-0" />
          {suggestions.map((definition) => (
            <TagSuggestion
              key={definition.id}
              definition={definition}
              onManage={manage}
              onAdd={(tag) => onTagsChange([...tags, tag])}
              text={text}
            />
          ))}
        </div>
      )}
      {canManage && (
        <ManageTagsDialog
          open={managing !== null}
          onOpenChange={(next) => !next && setManaging(null)}
          focusId={managing?.focusId}
          creating={managing?.creating}
          library={library}
          usedNames={usedNames}
          onCreate={onCreateDefinition}
          onUpdate={onUpdateDefinition}
          onDelete={onDeleteDefinition}
          labels={text}
        />
      )}
    </div>
  );
}

function ProjectDetails({
  values,
  languages,
  onNameChange,
  onDescriptionChange,
  onLanguageChange,
  onVersionNameChange,
  onTagsChange,
  tagLibrary,
  onCreateTagDefinition,
  onUpdateTagDefinition,
  onDeleteTagDefinition,
  canManageTags = true,
  onShufflePicture,
  onUploadPicture,
  onRemovePicture,
  pictureAccept,
  pending = false,
  readOnly = false,
  visibility,
  onVisibilityChange,
  labels,
  className
}: ProjectDetailsProps) {
  const text = { ...defaultProjectDetailsLabels, ...labels };
  const visibilityLabels: Record<ProjectVisibility, string> = {
    organization: text.visibilityOrganization,
    private: text.visibilityPrivate
  };
  const title = pending ? (
    <div className="flex flex-col gap-3 px-2">
      <Skeleton className="h-7 w-3/4" />
      <Skeleton className="h-5 w-1/2" />
    </div>
  ) : (
    <>
      <SavingInput
        aria-label={text.projectName}
        required
        multiline
        value={values.name}
        placeholder={text.untitledProject}
        readOnly={readOnly}
        onSave={onNameChange}
        className={cn(quietControl, "h-auto py-0.5 text-balance typo-h4!")}
      />
      <SavingInput
        aria-label={text.description}
        multiline
        value={values.description}
        placeholder={text.descriptionPlaceholder}
        readOnly={readOnly}
        onSave={onDescriptionChange}
        className={cn(
          quietControl,
          "h-auto max-h-[4.75rem] overflow-hidden py-0.5 text-muted-foreground typo-body-md! focus:max-h-none"
        )}
      />
    </>
  );
  return (
    <div
      data-slot="project-details"
      data-layout="page"
      className={cn("flex w-full flex-col gap-6 md:flex-row md:items-stretch", className)}
    >
      {pending ? (
        <Skeleton className="aspect-square w-40 shrink-0 self-start rounded-2xl sm:w-44 md:aspect-auto md:min-h-44 md:self-stretch" />
      ) : (
        <ProjectCoverField
          picture={values.picture}
          accept={pictureAccept}
          onShuffle={readOnly ? undefined : onShufflePicture}
          onUpload={readOnly ? undefined : onUploadPicture}
          onRemove={readOnly ? undefined : onRemovePicture}
          text={text}
          className="aspect-square w-40 shrink-0 self-start sm:w-44 md:aspect-auto md:min-h-44 md:self-stretch"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-5">
        <div className="-mx-2 flex flex-col">{title}</div>
        {onTagsChange && !readOnly && (
          <ProjectTagsField
            tags={values.tags ?? []}
            library={tagLibrary}
            pending={pending}
            onTagsChange={onTagsChange}
            onCreateDefinition={onCreateTagDefinition}
            onUpdateDefinition={onUpdateTagDefinition}
            onDeleteDefinition={onDeleteTagDefinition}
            canManageTags={canManageTags}
            labels={text}
          />
        )}
      </div>
      <dl className="flex shrink-0 flex-col gap-3 border-t border-border pt-5 md:w-44 md:justify-between md:gap-0 md:border-s md:border-t-0 md:ps-6 md:pt-0">
        <div className="flex flex-col gap-0.5">
          <dt>
            <label htmlFor="project-language" className="flex items-center gap-1.5 typo-caption text-muted-foreground">
              <TranslateIcon className="size-3.5 shrink-0" aria-hidden="true" />
              {text.language}
            </label>
          </dt>
          <dd className="-mx-2">
            {pending ? (
              <Skeleton className="mx-2 h-5 w-24" />
            ) : (
              <PropertySelect
                id="project-language"
                value={values.language}
                options={languages}
                placeholder={text.languagePlaceholder}
                required
                disabled={readOnly}
                text={text}
                className="w-auto max-w-full typo-body-md!"
                onChange={(language) => language && onLanguageChange(language)}
              />
            )}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt>
            <label htmlFor="project-version" className="flex items-center gap-1.5 typo-caption text-muted-foreground">
              <GitBranchIcon className="size-3.5 shrink-0" aria-hidden="true" />
              {text.version}
            </label>
          </dt>
          <dd className="-mx-2">
            <SavingInput
              id="project-version"
              required
              readOnly={readOnly || !onVersionNameChange}
              value={values.versionName}
              onSave={(name) => onVersionNameChange?.(name)}
              className={cn(quietControl, "typo-body-md!")}
            />
          </dd>
        </div>
        {visibility && onVisibilityChange && (
          <div className="flex flex-col gap-0.5">
            <dt>
              <label
                htmlFor="project-visibility"
                className="flex items-center gap-1.5 typo-caption text-muted-foreground"
              >
                {visibility === "private" ? (
                  <LockSimpleIcon className="size-3.5 shrink-0" aria-hidden="true" />
                ) : (
                  <UsersThreeIcon className="size-3.5 shrink-0" aria-hidden="true" />
                )}
                {text.visibility}
              </label>
            </dt>
            <dd className="-mx-2">
              <Select
                items={Object.entries(visibilityLabels).map(([value, label]) => ({ value, label }))}
                value={visibility}
                disabled={readOnly}
                onValueChange={(next) => (next === "organization" || next === "private") && onVisibilityChange(next)}
              >
                <SelectTrigger
                  id="project-visibility"
                  className={cn(
                    quietControl,
                    "w-auto max-w-full typo-body-md! [&>svg:last-child]:opacity-0 hover:[&>svg:last-child]:opacity-100 focus-visible:[&>svg:last-child]:opacity-100"
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="organization">{visibilityLabels.organization}</SelectItem>
                  <SelectItem value="private">{visibilityLabels.private}</SelectItem>
                </SelectContent>
              </Select>
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function ProjectCompactBar({
  picture,
  name,
  details = [],
  visible,
  onExpand,
  labels,
  className
}: {
  picture: ProjectPicture | null;
  name: string;
  details?: string[];
  visible: boolean;
  onExpand: () => void;
  labels?: Partial<ProjectDetailsLabels>;
  className?: string;
}) {
  const text = { ...defaultProjectDetailsLabels, ...labels };
  return (
    <button
      type="button"
      data-slot="project-compact-bar"
      aria-hidden={!visible || undefined}
      tabIndex={visible ? 0 : -1}
      title={text.showProjectDetails}
      onClick={onExpand}
      className={cn(
        "group/bar flex w-full items-center gap-3 rounded-2xl bg-card py-2 ps-2 pe-4 text-start ring-1 ring-foreground/5 transition-[opacity,translate] outline-none focus-visible:ring-3 focus-visible:ring-ring/50",
        visible
          ? "translate-y-0 opacity-100 delay-200 duration-[380ms] ease-(--vt-ease-enter)"
          : "pointer-events-none -translate-y-1 opacity-0 duration-150 ease-(--vt-ease-exit)",
        className
      )}
    >
      <ProjectPictureThumb picture={picture} size="sm" />
      <span className="min-w-0 flex-1 truncate typo-h6 text-foreground">{name}</span>
      {details.length > 0 && (
        <span className="hidden shrink-0 typo-body-sm text-muted-foreground md:block">{details.join(" · ")}</span>
      )}
      <CaretDownIcon
        className="size-4 shrink-0 text-muted-foreground transition-colors group-hover/bar:text-foreground"
        aria-hidden="true"
      />
    </button>
  );
}

export {
  defaultProjectDetailsLabels,
  ManageTagsDialog,
  PictureCredit,
  pictureSource,
  ProjectCompactBar,
  ProjectDetails,
  ProjectTagsField,
  ProjectPictureThumb,
  type ProjectDetailsLabels,
  type ProjectDetailsValues,
  type ProjectDimension,
  type ProjectDimensionValue,
  type ProjectVisibility,
  type ProjectTag,
  type ProjectTagDefinition,
  type ProjectPicture,
  type ProjectPictureSource
};
